/**
 * Scrollable message list with stick-to-bottom behavior (AI Elements approach:
 * track whether the user is near the bottom; auto-follow new content only when
 * they are; otherwise show a "jump to latest" affordance). An `aria-live`
 * region announces streaming updates.
 *
 * Renders items through a render-prop so a virtualizer can be slotted in later
 * without changing the contract.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Fab } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { UIMessage } from 'ai';

const NEAR_BOTTOM_PX = 80;

interface MessageListProps {
  messages: UIMessage[];
  renderItem: (message: UIMessage, isLast: boolean) => React.ReactNode;
  footer?: React.ReactNode;
  isStreaming: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ messages, renderItem, footer, isStreaming }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(true);

  const computeStuck = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setStuck(distance <= NEAR_BOTTOM_PX);
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Follow new content / streaming growth only while stuck to the bottom.
  useLayoutEffect(() => {
    if (stuck) scrollToBottom(false);
  }, [messages, footer, stuck, scrollToBottom]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (stuck) scrollToBottom(false);
    });
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [messages, stuck, scrollToBottom]);

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
      <Box
        ref={scrollRef}
        onScroll={computeStuck}
        sx={{
          height: '100%',
          overflowY: 'auto',
          px: 2,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {messages.map((m, i) => (
          <React.Fragment key={m.id}>{renderItem(m, i === messages.length - 1)}</React.Fragment>
        ))}
        {footer}
        <Box
          aria-live="polite"
          aria-atomic="false"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          {isStreaming ? 'Assistant is responding' : ''}
        </Box>
      </Box>

      {!stuck && (
        <Fab
          size="small"
          color="default"
          aria-label="Jump to latest"
          onClick={() => scrollToBottom(true)}
          sx={{ position: 'absolute', bottom: 12, right: 12, boxShadow: 2 }}
        >
          <KeyboardArrowDownIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MessageList;
